import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Popover,
  Tooltip,
} from 'react-bootstrap';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import { createTooltipProps } from '../utils/fieldTooltips';
import GridPagination from '../components/GridPagination';

function HireCategoryGroupModal({ hireCategoryGroup, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    group_code: '',
    group_name: '',
    description: '',
    active: true,
  });

  useEffect(() => {
    if (hireCategoryGroup) {
      setFormData({
        group_code: hireCategoryGroup.group_code || '',
        group_name: hireCategoryGroup.group_name || '',
        description: hireCategoryGroup.description || '',
        active: hireCategoryGroup.active !== undefined ? hireCategoryGroup.active : true,
      });
    } else {
      // Reset form for new hire category group
      setFormData({
        group_code: '',
        group_name: '',
        description: '',
        active: true,
      });
    }
  }, [hireCategoryGroup]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updatedFormData = {
      ...formData,
      [e.target.name]: value,
    };

    setFormData(updatedFormData);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setModalError('');

    // Comprehensive validation using validation rules
    const errors = validateForm(formData, ValidationRules.hireCategoryGroup);
    setValidationErrors(errors);

    if (hasErrors(errors)) {
      setModalError(formatErrors(errors));
      return;
    }

    onSave(formData);
  };

  // Helper component for form fields with validation popover and tooltips
  const ValidatedFormControl = ({
    name,
    label,
    required,
    children,
    tooltipDescription,
    ...props
  }) => {
    const hasError = validationErrors[name];
    const popover = hasError ? (
      <Popover id={`popover-${name}`}>
        <Popover.Header as="h3">Validation Error</Popover.Header>
        <Popover.Body>{validationErrors[name]}</Popover.Body>
      </Popover>
    ) : null;

    const tooltip = tooltipDescription ? (
      <Tooltip {...createTooltipProps(name)}>{tooltipDescription}</Tooltip>
    ) : null;

    const formControl = children || (
      <Form.Control
        {...props}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        isInvalid={!!hasError}
        {...createTooltipProps(name)}
      />
    );

    const wrappedControl = hasError ? (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
        {formControl}
      </OverlayTrigger>
    ) : tooltipDescription ? (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {formControl}
      </OverlayTrigger>
    ) : (
      formControl
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {wrappedControl}
      </Form.Group>
    );
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {hireCategoryGroup ? 'Edit Hire Category Group' : 'Add New Hire Category Group'}
        </Modal.Title>
      </Modal.Header>

      {modalError && (
        <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {modalError}
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Group Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="group_name"
                  type="text"
                  value={formData.group_name || ''}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.group_name}
                />
                {validationErrors.group_name && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.group_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  value={formData.description || ''}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.description}
                />
                {validationErrors.description && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.description}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('active')}>
                  Controls whether the group is active and available for use
                </Tooltip>
              }
            >
              <Form.Check
                type="checkbox"
                name="active"
                label="Active"
                checked={formData.active}
                onChange={handleChange}
                {...createTooltipProps('active')}
              />
            </OverlayTrigger>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {hireCategoryGroup ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function HireCategoryGroups() {
  const [hireCategoryGroups, setHireCategoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHireCategoryGroup, setEditingHireCategoryGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchHireCategoryGroups = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-category-groups', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setHireCategoryGroups(response.data.hireCategoryGroups);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch hire category groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHireCategoryGroups();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHireCategoryGroups(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchHireCategoryGroups(newPage, searchTerm);
  };

  const handleAddHireCategoryGroup = () => {
    setEditingHireCategoryGroup(null);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleEditHireCategoryGroup = (hireCategoryGroup) => {
    setEditingHireCategoryGroup(hireCategoryGroup);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSaveHireCategoryGroup = async (hireCategoryGroupData) => {
    try {
      setError('');

      if (editingHireCategoryGroup) {
        await axios.put(
          `/api/hire-category-groups/${editingHireCategoryGroup.id}`,
          hireCategoryGroupData
        );
        setSuccess('Hire category group updated successfully');
      } else {
        await axios.post('/api/hire-category-groups', hireCategoryGroupData);
        setSuccess('Hire category group created successfully');
      }

      setShowModal(false);
      setEditingHireCategoryGroup(null);
      await fetchHireCategoryGroups(pagination.page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save hire category group');
    }
  };

  const handleDeleteHireCategoryGroup = async (id) => {
    if (window.confirm('Are you sure you want to delete this hire category group?')) {
      try {
        await axios.delete(`/api/hire-category-groups/${id}`);
        setSuccess('Hire category group deleted successfully');
        fetchHireCategoryGroups(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete hire category group');
      }
    }
  };

  const handleDeleteAllHireCategoryGroups = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL hire category groups? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your hire category group records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/hire-category-groups');
          setSuccess(
            `All hire category groups deleted successfully (${response.data.deletedCount} records)`
          );
          fetchHireCategoryGroups(1, '');
        } catch (error) {
          setError('Failed to delete all hire category groups');
        }
      }
    }
  };

  return (
    <Container fluid>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1rem',
          marginBottom: '20px',
        }}
      >
        <div className="d-flex align-items-center">
          <i className="fas fa-layer-group me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
          <div>
            <h1 className="mb-0">Hire Category Groups</h1>
            <p className="text-muted mb-0">Organize hire categories into logical groups</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', position: 'relative', zIndex: 1 }}>
          <button className="btn btn-primary" onClick={handleAddHireCategoryGroup}>
            Add Hire Category Group
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDeleteAllHireCategoryGroups}
            disabled={hireCategoryGroups.length === 0}
          >
            Delete All
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button
            onClick={() => setError('')}
            style={{ float: 'right', background: 'none', border: 'none' }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button
            onClick={() => setSuccess('')}
            style={{ float: 'right', background: 'none', border: 'none' }}
          >
            ×
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="text"
              placeholder="Search hire category groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              <i className="bi bi-search me-1"></i>
              Search
            </button>
          </form>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading hire category groups...
            </div>
          ) : hireCategoryGroups.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-collection"></i>
              <p className="mb-0">No hire category groups found</p>
            </div>
          ) : (
            <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="border-0">Group Name</th>
                    <th className="border-0">Description</th>
                    <th className="border-0">Status</th>
                    <th className="border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hireCategoryGroups.map((hireCategoryGroup) => (
                    <tr key={hireCategoryGroup.id}>
                      <td>
                        <strong>{hireCategoryGroup.group_name}</strong>
                      </td>
                      <td>{hireCategoryGroup.description}</td>
                      <td>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: hireCategoryGroup.active ? '#28a745' : '#6c757d',
                            color: 'white',
                          }}
                        >
                          {hireCategoryGroup.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditHireCategoryGroup(hireCategoryGroup)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteHireCategoryGroup(hireCategoryGroup.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && hireCategoryGroups.length > 0 && (
          <div className="card-footer">
            <GridPagination
              page={pagination.page}
              pages={pagination.pages}
              onPageChange={handlePageChange}
              size="sm"
              ulClassName="mb-0 justify-content-center"
              wrapperClassName=""
              aria-label="Hire category groups pagination"
              prevLabel={<i className="bi bi-chevron-left"></i>}
              nextLabel={<i className="bi bi-chevron-right"></i>}
            />
          </div>
        )}
      </div>

      {showModal && (
        <HireCategoryGroupModal
          hireCategoryGroup={editingHireCategoryGroup}
          onClose={() => {
            setShowModal(false);
            setEditingHireCategoryGroup(null);
            setError('');
            setSuccess('');
          }}
          onSave={handleSaveHireCategoryGroup}
        />
      )}
    </Container>
  );
}

export default HireCategoryGroups;
